/**
 * Ledger Hooks - React hooks for ledger data access
 * Epic 4.5: Read-only Ledger Views API
 * 
 * Uses React Query for request deduplication, caching, and automatic retries
 */

import { useQuery } from "@tanstack/react-query";
import {
  getJobLedgerEntries,
  getJobLedgerSummary,
  getCustomerLedgerEntries,
  getCustomerLedgerSummary,
  getCustomerOutstandingBalance,
  getInvoiceLedgerEntries,
  getProviderLedgerEntries,
  getProviderLedgerSummary,
  getJobSummary, // Alias for getJobLedgerSummary
} from "../services/ledgerService.js";

/**
 * Hook for fetching job ledger summary
 * @param {string} jobId - Job ID
 * @param {Date|string} [asOfDate] - Optional cutoff date
 * @returns {Object} Summary data and loading state
 */
export const useJobSummary = (jobId, asOfDate = null) => {
  const asOfDateStr = asOfDate ? (asOfDate instanceof Date ? asOfDate.toISOString() : asOfDate) : null;
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["jobSummary", jobId, asOfDateStr],
    queryFn: async () => {
      if (!jobId) return null;
      const result = await getJobLedgerSummary(jobId, asOfDate);
      if (result.success) {
        return result.data;
      }
      throw new Error(result.error || "Failed to load job summary");
    },
    enabled: !!jobId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 1,
  });

  return {
    summary: data,
    loading: isLoading,
    error: error?.message || null,
    refetch,
  };
};

/**
 * Hook for fetching job ledger entries (timeline)
 * Uses /ledger/jobs/:jobId/entries endpoint
 * @param {string} jobId - Job ID
 * @param {Object} options - Query options
 * @param {Date|string} [options.asOfDate] - Cutoff date
 * @param {number} [options.limit] - Max entries
 * @param {string} [options.entryType] - Filter by entry type
 * @returns {Object} Timeline data and loading state
 */
export const useJobTimeline = (jobId, options = {}) => {
  const asOfDateStr = options.asOfDate 
    ? (options.asOfDate instanceof Date ? options.asOfDate.toISOString() : options.asOfDate)
    : null;
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["jobTimeline", jobId, asOfDateStr, options.limit, options.entryType],
    queryFn: async () => {
      if (!jobId) return null;
      const result = await getJobLedgerEntries(jobId, options);
      
      if (result.success) {
        // Format entries for timeline display
        const formattedEntries = (result.data || []).map((entry) => ({
          ...entry,
          entryTypeLabel: formatEntryType(entry.entryType),
          directionLabel: entry.direction === "DEBIT" ? "Outgoing" : "Incoming",
        }));
        return {
          entries: formattedEntries,
          entryCount: formattedEntries.length,
          asOf: options.asOfDate || new Date().toISOString(),
        };
      }
      throw new Error(result.error || "Failed to load job timeline");
    },
    enabled: !!jobId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 1,
  });

  return {
    timeline: data,
    loading: isLoading,
    error: error?.message || null,
    refetch,
  };
};

/**
 * Format entry type for human-readable display
 */
function formatEntryType(entryType) {
  const typeMap = {
    CUSTOMER_CHARGE: "Customer Charge",
    CUSTOMER_PAYMENT: "Customer Payment",
    CUSTOMER_REFUND: "Customer Refund",
    PROVIDER_PAYOUT: "Provider Payout",
    PROVIDER_RETENTION_HOLD: "Retention Hold",
    PROVIDER_RETENTION_RELEASE: "Retention Release",
    RETENTION_RELEASE: "Retention Release",
    JOB_ADJUSTMENT: "Job Adjustment",
    PLATFORM_FEE: "Platform Fee",
    PRODUCT_SALE: "Product Sale",
    REFUND: "Refund",
  };
  return typeMap[entryType] || entryType;
}

/**
 * Hook for fetching customer ledger entries
 * @param {string} customerId - Customer ID
 * @param {Object} options - Query options
 * @param {Date|string} [options.asOfDate] - Cutoff date
 * @param {number} [options.limit] - Max entries
 * @param {string} [options.entryType] - Filter by entry type
 * @returns {Object} Ledger entries data and loading state
 */
export const useCustomerLedger = (customerId, options = {}) => {
  const asOfDateStr = options.asOfDate 
    ? (options.asOfDate instanceof Date ? options.asOfDate.toISOString() : options.asOfDate)
    : null;
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["customerLedger", customerId, asOfDateStr, options.limit, options.entryType],
    queryFn: async () => {
      if (!customerId) return null;
      const result = await getCustomerLedgerEntries(customerId, options);
      if (result.success) {
        return result;
      }
      throw new Error(result.error || "Failed to load customer ledger entries");
    },
    enabled: !!customerId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 1,
  });

  return {
    data,
    isLoading,
    error: error?.message || null,
    refetch,
  };
};

/**
 * Hook for fetching customer ledger summary
 * @param {string} customerId - Customer ID
 * @param {Date|string} asOfDate - Cutoff date
 * @returns {Object} Summary data with React Query-like interface
 */
export const useCustomerLedgerSummary = (customerId, asOfDate) => {
  const asOfDateStr = asOfDate 
    ? (asOfDate instanceof Date ? asOfDate.toISOString() : asOfDate)
    : null;
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["customerLedgerSummary", customerId, asOfDateStr],
    queryFn: async () => {
      if (!customerId) return null;
      const result = await getCustomerLedgerSummary(customerId, asOfDate);
      if (result.success) {
        return result;
      }
      throw new Error(result.error || "Failed to load customer ledger summary");
    },
    enabled: !!customerId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 1,
  });

  return {
    data,
    isLoading,
    error: error?.message || null,
    refetch,
  };
};

/**
 * Hook for fetching customer outstanding balance
 * @param {string} customerId - Customer ID
 * @param {Date|string} asOfDate - Cutoff date
 * @returns {Object} Outstanding balance data
 */
export const useCustomerOutstandingBalance = (customerId, asOfDate) => {
  const asOfDateStr = asOfDate 
    ? (asOfDate instanceof Date ? asOfDate.toISOString() : asOfDate)
    : null;
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["customerOutstandingBalance", customerId, asOfDateStr],
    queryFn: async () => {
      if (!customerId) return null;
      const result = await getCustomerOutstandingBalance(customerId, asOfDate);
      if (result.success) {
        return result;
      }
      throw new Error(result.error || "Failed to load customer outstanding balance");
    },
    enabled: !!customerId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 1,
  });

  return {
    data,
    isLoading,
    error: error?.message || null,
    refetch,
  };
};

/**
 * Hook for fetching invoice ledger entries
 * @param {string} invoiceId - Invoice ID
 * @param {Object} options - Query options
 * @param {Date|string} [options.asOfDate] - Cutoff date
 * @param {number} [options.limit] - Max entries
 * @param {string} [options.entryType] - Filter by entry type
 * @returns {Object} Ledger entries data and loading state
 */
export const useInvoiceLedger = (invoiceId, options = {}) => {
  const asOfDateStr = options.asOfDate 
    ? (options.asOfDate instanceof Date ? options.asOfDate.toISOString() : options.asOfDate)
    : null;
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["invoiceLedger", invoiceId, asOfDateStr, options.limit, options.entryType],
    queryFn: async () => {
      if (!invoiceId) return null;
      const result = await getInvoiceLedgerEntries(invoiceId, options);
      if (result.success) {
        return result;
      }
      throw new Error(result.error || "Failed to load invoice ledger entries");
    },
    enabled: !!invoiceId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 1,
  });

  return {
    data,
    isLoading,
    error: error?.message || null,
    refetch,
  };
};

/**
 * Hook for fetching job ledger entries (React Query-like interface)
 * @param {string} jobId - Job ID
 * @param {Object} options - Query options
 * @param {Date|string} [options.asOfDate] - Cutoff date
 * @param {number} [options.limit] - Max entries
 * @param {string} [options.entryType] - Filter by entry type
 * @returns {Object} React Query-like interface
 */
export const useJobLedger = (jobId, options = {}) => {
  const asOfDateStr = options.asOfDate 
    ? (options.asOfDate instanceof Date ? options.asOfDate.toISOString() : options.asOfDate)
    : null;
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["jobLedger", jobId, asOfDateStr, options.limit, options.entryType],
    queryFn: async () => {
      if (!jobId) return null;
      const result = await getJobLedgerEntries(jobId, options);
      if (result.success) {
        return result;
      }
      throw new Error(result.error || "Failed to load job ledger entries");
    },
    enabled: !!jobId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 1,
  });

  return {
    data,
    isLoading,
    error: error?.message || null,
    refetch,
  };
};

/**
 * Hook for fetching job ledger summary (React Query-like interface)
 * @param {string} jobId - Job ID
 * @param {Date|string} asOfDate - Cutoff date
 * @returns {Object} Summary data with React Query-like interface
 */
export const useJobLedgerSummary = (jobId, asOfDate) => {
  const asOfDateStr = asOfDate 
    ? (asOfDate instanceof Date ? asOfDate.toISOString() : asOfDate)
    : null;
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["jobLedgerSummary", jobId, asOfDateStr],
    queryFn: async () => {
      if (!jobId) return null;
      const result = await getJobLedgerSummary(jobId, asOfDate);
      if (result.success) {
        return result;
      }
      throw new Error(result.error || "Failed to load job ledger summary");
    },
    enabled: !!jobId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 1,
  });

  return {
    data,
    isLoading,
    error: error?.message || null,
    refetch,
  };
};

/**
 * Hook for fetching provider/payee ledger entries
 * @param {string} providerId - Provider ID
 * @param {Object} options - Query options
 * @param {Date|string} [options.asOfDate] - Cutoff date
 * @param {number} [options.limit] - Max entries
 * @param {string} [options.entryType] - Filter by entry type
 * @returns {Object} Ledger entries data and loading state
 */
export const useProviderLedger = (providerId, options = {}) => {
  const asOfDateStr = options.asOfDate 
    ? (options.asOfDate instanceof Date ? options.asOfDate.toISOString() : options.asOfDate)
    : null;
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["providerLedger", providerId, asOfDateStr, options.limit, options.entryType],
    queryFn: async () => {
      if (!providerId) return null;
      const result = await getProviderLedgerEntries(providerId, options);
      if (result.success) {
        return result;
      }
      throw new Error(result.error || "Failed to load provider ledger entries");
    },
    enabled: !!providerId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 1,
  });

  return {
    data,
    isLoading,
    error: error?.message || null,
    refetch,
  };
};

/**
 * Hook for fetching provider/payee ledger summary
 * @param {string} providerId - Provider ID
 * @param {Date|string} asOfDate - Cutoff date
 * @returns {Object} Summary data with React Query-like interface
 */
export const useProviderLedgerSummary = (providerId, asOfDate) => {
  const asOfDateStr = asOfDate 
    ? (asOfDate instanceof Date ? asOfDate.toISOString() : asOfDate)
    : null;
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["providerLedgerSummary", providerId, asOfDateStr],
    queryFn: async () => {
      if (!providerId) return null;
      const result = await getProviderLedgerSummary(providerId, asOfDate);
      if (result.success) {
        return result;
      }
      throw new Error(result.error || "Failed to load provider ledger summary");
    },
    enabled: !!providerId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 1,
  });

  return {
    data,
    isLoading,
    error: error?.message || null,
    refetch,
  };
};
